import React from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  useEvent,
  useTimeoutRef,
  GuestsTaxes,
  GuestTaxExemption,
  Housing,
  QUERY_CACHE_KEYS,
} from '@chekin/core';
import {API, CalculateGuestTaxPayload} from '@chekin/api';
import {useSummary} from '../../context/summary';
import {useReservation} from '../../context/reservation';
import {useChekinSDK} from '../../context/ChekinSDK';
import {useIsMounted} from '@chekin/guestapp-ui-kit';

const CACHE_TIME = 60;
const STALE_TIME = 30;

function getSeasonsIds(housing?: Housing) {
  if (!housing) {
    return [];
  }

  return housing?.seasons || [];
}

function useFetchSeason() {
  const {isTokenValid} = useChekinSDK();
  const {housing} = useReservation();
  const highSeasonId = getSeasonsIds(housing)[0];

  const queryData = useQuery({
    queryKey: [QUERY_CACHE_KEYS.season, highSeasonId],
    queryFn: () => API.taxes.getSeason(highSeasonId),
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * CACHE_TIME,
    staleTime: 1000 * 60 * STALE_TIME,
    enabled: Boolean(isTokenValid && highSeasonId),
  });

  const isLoadingSeason =
    queryData.isFetching ||
    Boolean(queryData.isLoading && !housing?.id) ||
    Boolean(queryData.isLoading && highSeasonId);

  return {queryData, isLoadingSeason};
}

function useInitGuestTaxes() {
  const {reservationId} = useReservation();
  const queryClient = useQueryClient();

  const {mutate} = useMutation<GuestsTaxes, Error, {reservation_id: string}>({
    mutationFn: payload => API.taxes.initGuestTaxes(payload),
    onSuccess: data => {
      queryClient.setQueryData([QUERY_CACHE_KEYS.seasonGuests], [data]);
      void queryClient.refetchQueries({
        queryKey: [QUERY_CACHE_KEYS.paymentsCart],
      });
    },
  });

  const initGuestTaxes = React.useCallback(
    (data?: GuestsTaxes) => {
      if (data?.id || !reservationId) return;
      return mutate({reservation_id: reservationId});
    },
    [mutate, reservationId],
  );

  useFetchGuestsTaxes({onSuccess: initGuestTaxes});

  return {initGuestTaxes};
}

type UseFetchGuestsTaxesProps = {
  onSuccess?: (data?: GuestsTaxes) => void;
  onError?: (error: Error) => void;
  disabled?: boolean;
};
function useFetchGuestsTaxes({onSuccess, onError}: UseFetchGuestsTaxesProps = {}) {
  const {isTokenValid} = useChekinSDK();
  const {data: reservation} = useReservation();
  const isMounted = useIsMounted();
  const {data} = useSummary();
  const reservationId = reservation?.id;
  const handleSuccess = useEvent(onSuccess);
  const handleError = useEvent(onError);

  const query = useQuery<GuestsTaxes[], Error, GuestsTaxes>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [QUERY_CACHE_KEYS.seasonGuests],
    queryFn: () => API.taxes.getSeasonGuests(`reservation_id=${reservationId}`),
    select: data => data?.[0],
    enabled: Boolean(data?.taxes?.active && isTokenValid && reservationId),
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * CACHE_TIME,
    staleTime: 1000 * 60 * STALE_TIME,
  });

  React.useEffect(() => {
    if (query.isSuccess) {
      handleSuccess(query.data);
    }
  }, [query.isSuccess, handleSuccess, query.data]);

  React.useEffect(() => {
    if (query.error && isMounted.current) {
      handleError(query.error);
    }
  }, [query.error, handleError, isMounted]);

  return query;
}

type MutateOptions = {
  successDelay?: number;
};

type UseMutateGuestTaxProps = {
  guestsTaxesId?: string;
  onError?: (error: Error) => void;
};
function useMutateGuestTax({guestsTaxesId, onError}: UseMutateGuestTaxProps) {
  const {data: reservation} = useReservation();
  const queryClient = useQueryClient();
  const isMounted = useIsMounted();
  const delayTimeoutRef = useTimeoutRef();

  const {mutate: guestTaxesMutate, isPending} = useMutation<
    GuestsTaxes,
    Error,
    CalculateGuestTaxPayload & {options?: MutateOptions},
    MutateOptions
  >({
    mutationFn: ({options: _, ...payload}) => API.taxes.calculateGuestTax(payload),
    onMutate: ({options}) => {
      return options || {};
    },
    onSuccess: (data, _, context) => {
      if (context?.successDelay) {
        delayTimeoutRef.current = setTimeout(() => {
          queryClient.setQueryData([QUERY_CACHE_KEYS.seasonGuests], [data]);
        }, context?.successDelay);
        return;
      }
      queryClient.setQueryData([QUERY_CACHE_KEYS.seasonGuests], [data]);
    },
    onError: error => {
      if (!isMounted.current) {
        return;
      }

      if (error && onError) {
        onError(error);
      }
    },
  });

  const getTaxCalculationPayload = React.useCallback(
    (guestList: GuestTaxExemption[]) => {
      return {
        reservation_id: reservation?.id || '',
        guest_list: guestList,
      };
    },
    [reservation?.id],
  );

  const mutateGuestTax = React.useCallback(
    async (
      guestList: GuestTaxExemption[],
      {onSuccess, successDelay}: {onSuccess?: () => void; successDelay?: number},
    ) => {
      const payload = getTaxCalculationPayload(guestList);

      if (guestsTaxesId) {
        guestTaxesMutate(
          {...payload, id: guestsTaxesId, options: {successDelay}},
          {
            onSuccess,
          },
        );
      } else {
        guestTaxesMutate(
          {...payload, options: {successDelay}},
          {
            onSuccess,
          },
        );
      }
    },
    [getTaxCalculationPayload, guestTaxesMutate, guestsTaxesId],
  );

  const confirmTaxesSetup = React.useCallback(
    (options?: Parameters<typeof mutateGuestTax>[1]) => {
      if (!reservation?.id) return;
      guestTaxesMutate(
        {status: 'COMPLETE', id: guestsTaxesId, reservation_id: reservation.id},
        {
          ...options,
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey: [QUERY_CACHE_KEYS.paymentsCart],
            });
            void queryClient.invalidateQueries({
              queryKey: [QUERY_CACHE_KEYS.summary],
            });
            options?.onSuccess?.();
          },
        },
      );
    },
    [guestTaxesMutate, guestsTaxesId, queryClient, reservation?.id],
  );

  return {mutateGuestTax, confirmTaxesSetup, isLoading: isPending};
}

export {useFetchSeason, useFetchGuestsTaxes, useMutateGuestTax, useInitGuestTaxes};
export type {MutateOptions};
